import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { toast } from 'sonner';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const { login, verifyMFA, setAccessToken, isAuthenticated, loading: authLoading } = useAuth();
  
  const [step, setStep] = useState('login'); // 'login' | 'mfa'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfaFailedAttempts, setMfaFailedAttempts] = useState(0);
  const [mfaCooldownUntil, setMfaCooldownUntil] = useState(0);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (mfaCooldownUntil <= Date.now()) return undefined;

    const intervalId = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(intervalId);
  }, [mfaCooldownUntil]);

  const cooldownSeconds = Math.max(0, Math.ceil((mfaCooldownUntil - now) / 1000));
  const isMfaCooldownActive = cooldownSeconds > 0;

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await login(email, password);
      
      if (response.mfa_required) {
        setTempToken(response.temp_token);
        setStep('mfa');
        setMfaFailedAttempts(0);
        setMfaCooldownUntil(0);
        toast.info('Please enter your MFA code');
      } else {
        await setAccessToken(response.access_token);
        toast.success('Login successful!');
        navigate('/admin/dashboard');
      }
    } catch (error) {
      console.error('Login failed:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleMFAVerify = async () => {
    if (mfaCode.length !== 6) return;
    if (isMfaCooldownActive) return;
    setLoading(true);

    try {
      await verifyMFA(mfaCode, tempToken);
      setMfaFailedAttempts(0);
      setMfaCooldownUntil(0);
      toast.success('Login successful!');
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('MFA verification failed:', error);
      toast.error(error.response?.data?.detail || 'Invalid MFA code');
      setMfaCode('');

      const updatedAttempts = mfaFailedAttempts + 1;
      setMfaFailedAttempts(updatedAttempts);

      if (updatedAttempts >= 3) {
        const cooldownMs = 30 * 1000;
        setMfaCooldownUntil(Date.now() + cooldownMs);
        setMfaFailedAttempts(0);
        toast.warning('Too many failed attempts. Please wait 30 seconds before trying again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    await handleMFAVerify();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="noise-overlay" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-4">
            <h1 className="font-heading text-3xl font-semibold">AIDreamSpaces</h1>
          </Link>
          <p className="text-muted-foreground">Admin Portal</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 md:p-8 shadow-lg">
          {step === 'login' ? (
            <form onSubmit={handleLogin} className="space-y-6" data-testid="login-form">
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pr-10"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    data-testid="toggle-password"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full rounded-full"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          ) : (
            <form className="space-y-6" data-testid="mfa-form" onSubmit={handleMfaSubmit}>
              <div className="text-center">
                <Shield className="h-12 w-12 mx-auto mb-4 text-primary" />
                <h2 className="font-heading text-2xl font-semibold mb-2">Two-Factor Authentication</h2>
                <p className="text-muted-foreground text-sm">
                  Enter the 6-digit code from your authenticator app
                </p>
                {isMfaCooldownActive && (
                  <p className="text-xs text-destructive mt-2" data-testid="mfa-cooldown-message">
                    Please wait {cooldownSeconds}s before trying again.
                  </p>
                )}
              </div>

              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={mfaCode}
                  onChange={setMfaCode}
                  data-testid="mfa-code-input"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button
                type="submit"
                className="w-full rounded-full"
                disabled={loading || mfaCode.length !== 6 || isMfaCooldownActive}
                data-testid="mfa-submit"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </Button>

              <button
                type="button"
                onClick={() => {
                  setStep('login');
                  setMfaCode('');
                  setMfaFailedAttempts(0);
                  setMfaCooldownUntil(0);
                }}
                className="w-full text-sm text-muted-foreground hover:text-foreground"
                data-testid="back-to-login"
              >
                Back to login
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-sm text-muted-foreground mt-6">
          <Link to="/" className="hover:text-foreground transition-colors">
            ← Back to Gallery
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
